import React, { useEffect, useState } from 'react';
import { Switch, Input, Button, Alert, Spin } from 'antd';
import { PrinterOutlined, EditOutlined } from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import { avecLibelleTraduit } from '../../i18n/libelles';
import { can, HAB } from '../../variables/constants';
import {
  REGLAGES_PAR_DEFAUT, CLES_PARAMETRES, IndiceReedition,
} from '../sinistres/indiceReedition';
import { oublierReglagesIndices } from '../sinistres/useReglagesIndices';

/**
 * Indices portés par les documents imprimables du sinistre : fiche de
 * déclaration, attestation de déclaration, attestation de non-garantie.
 *
 * Deux faits distincts, deux réglages. Les confondre sous une seule marque était
 * le défaut d'origine : un document simplement réimprimé portait le signe de la
 * modification.
 */
const INDICES = [
  {
    cle: 'modification',
    icone: <EditOutlined />,
    cleActif: CLES_PARAMETRES.modificationActif,
    cleLibelle: CLES_PARAMETRES.modificationLibelle,
    descriptionActif: "Afficher un indice sur les documents dont la declaration a ete modifiee",
    descriptionLibelle: "Lettre de l'indice de modification",
  },
  {
    cle: 'impression',
    icone: <PrinterOutlined />,
    cleActif: CLES_PARAMETRES.impressionActif,
    cleLibelle: CLES_PARAMETRES.impressionLibelle,
    descriptionActif: "Afficher un indice sur les documents deja imprimes",
    descriptionLibelle: "Lettre de l'indice d'impression",
  },
];
avecLibelleTraduit(INDICES, 'parametres:documents.indices.titres', { cle: 'cle', champ: 'titre' });
avecLibelleTraduit(INDICES, 'parametres:documents.indices.aides', { cle: 'cle', champ: 'aide' });

export default function ParametresDocuments() {
  const { t } = useTranslation('parametres');
  // Le serveur exige PARAMETRE_UPDATE sur l'ecriture ; l'ecran le reflete.
  const habilite = can(HAB.PARAMETRE_UPDATE);

  const [existants, setExistants] = useState({});
  const [reglages, setReglages] = useState(REGLAGES_PAR_DEFAUT);
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/v1/parametres');
        const tous = res?.data?.data || [];
        if (ignore) return;

        const parCle = {};
        tous.forEach((p) => { parCle[p.cle] = p; });
        setExistants(parCle);

        const valeur = (cle) => parCle[cle]?.valeur;
        const actif = (cle, defaut) => {
          const v = valeur(cle);
          return v === undefined || v === null || v === '' ? defaut : String(v) === 'true';
        };
        setReglages({
          modificationActif: actif(CLES_PARAMETRES.modificationActif, REGLAGES_PAR_DEFAUT.modificationActif),
          modificationLibelle: valeur(CLES_PARAMETRES.modificationLibelle) || REGLAGES_PAR_DEFAUT.modificationLibelle,
          impressionActif: actif(CLES_PARAMETRES.impressionActif, REGLAGES_PAR_DEFAUT.impressionActif),
          impressionLibelle: valeur(CLES_PARAMETRES.impressionLibelle) || REGLAGES_PAR_DEFAUT.impressionLibelle,
        });
      } catch {
        // Les defauts sont deja en place : l'ecran reste utilisable.
      } finally {
        if (!ignore) setChargement(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const enregistrer = async () => {
    if (!habilite) return;
    setEnregistrement(true);
    try {
      const aEcrire = INDICES.flatMap((i) => {
        const actif = reglages[`${i.cle}Actif`];
        const libelle = String(reglages[`${i.cle}Libelle`] ?? '').trim()
          || REGLAGES_PAR_DEFAUT[`${i.cle}Libelle`];
        return [
          { cle: i.cleActif, valeur: String(actif), description: i.descriptionActif },
          { cle: i.cleLibelle, valeur: libelle, description: i.descriptionLibelle },
        ];
      });

      await Promise.all(aEcrire.map(({ cle, valeur, description }) => {
        const payload = { typeParametre: 'SYSTEM', cle, valeur, description };
        const existant = existants[cle];
        return existant
          ? axiosInstance.put(`/v1/parametres/${existant.parametreTrackingId}`, payload)
          : axiosInstance.post('/v1/parametres', payload);
      }));

      // Les documents lisent ces reglages par un cache de module : sans cet oubli,
      // il faudrait recharger l'application pour voir l'effet du changement.
      oublierReglagesIndices();
      toast.success(t('documents.messages.reglagesEnregistres'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('documents.messages.echecEnregistrement'));
    } finally {
      setEnregistrement(false);
    }
  };

  if (chargement) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;

  return (
    <div style={{ maxWidth: 760 }}>
      {!habilite && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message={t('documents.page.consultationSeule')}
          description={t('documents.page.habilitationRequise')}
        />
      )}

      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>
        <Trans t={t} i18nKey="documents.page.introduction" components={{ 1: <em /> }} />
      </p>

      {INDICES.map((i) => (
        <div
          key={i.cle}
          style={{
            border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '16px 18px', marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                {i.icone} {i.titre}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{i.aide}</div>
            </div>
            <Switch
              checked={reglages[`${i.cle}Actif`]}
              disabled={!habilite}
              checkedChildren={t('documents.indice.oui')} unCheckedChildren={t('documents.indice.non')}
              onChange={(v) => setReglages((r) => ({ ...r, [`${i.cle}Actif`]: v }))}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>{t('documents.indice.lettreAffichee')}</span>
            <Input
              value={reglages[`${i.cle}Libelle`]}
              disabled={!habilite || !reglages[`${i.cle}Actif`]}
              maxLength={2}
              style={{ width: 72, textAlign: 'center' }}
              onChange={(e) => setReglages((r) => ({ ...r, [`${i.cle}Libelle`]: e.target.value }))}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>{t('documents.indice.apercu')}</span>
            <IndiceReedition
              libelle={String(reglages[`${i.cle}Libelle`] ?? '').trim()
                || REGLAGES_PAR_DEFAUT[`${i.cle}Libelle`]}
              title={i.titre}
            />
          </div>
        </div>
      ))}

      <Button
        type="primary" loading={enregistrement} disabled={!habilite}
        onClick={enregistrer}
        style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
      >
        {t('documents.actions.enregistrer')}
      </Button>
    </div>
  );
}
